import { useState } from "react";
import PaymentsAndCosts from "./components/PaymentsAndCosts";

import Lang from "../../components/Lang";

import DateChoice from "../../components/DateChoice";
import Loading from "../../Loading";
import sdate from "@timclark/sdate";
import useDBO from "../../hooks/useDBO";
import { Wallet, CreditCard } from "lucide-react";
import { usePrimaryColor } from "../../components/PrimaryColorContext";


/**
 * Money page component.
 * Displays payment and cost information for cash and internet payments.
 * Uses tailwind utility classes with flat premium design language.
 *
 * @author Forge
 * @date 2026-04-08
 * @version 2.0
 */
export default function Money() {
  const [dt, setDt] = useState(new sdate());
  const { primaryColor } = usePrimaryColor();
  const payments = useDBO(`/get-payments/${dt}`)
  const costs = useDBO(`/costs?tDate=${dt}`);
  if(payments.loading || costs.loading) return <Loading />

  const cashPayments = payments.filter((p) => p.type === 0);
  const internetPayments = payments.filter((p) => p.type === 1);
  const totalCash = cashPayments.reduce((a, b) => a + b.amount, 0);
  const totalInternet = internetPayments.reduce((a, b) => a + b.amount, 0);

  return (
    <div className="p-6 max-w-[1600px] mx-auto min-h-screen bg-[#f8fafc] text-[#1e293b]">
      <div className="flex justify-between items-start mb-6 pb-4 border-b border-[#e2e8f0] gap-8">
        <div className="flex flex-col">
          <h1 className="text-[2rem] font-extrabold text-[#0f172a] tracking-tight m-0">
            <Lang w="money" />
          </h1>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-[10px] px-3 py-1.5 shadow-sm">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center bg-${primaryColor}-800`}>
                <Wallet size={15} className="text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-[0.6rem] font-bold text-slate-400 uppercase tracking-tight leading-none">
                  <Lang w="cash" />
                </span>
                <span className="text-[0.8rem] font-extrabold text-slate-800 leading-tight">
                  <VNDInline value={totalCash} />
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-[10px] px-3 py-1.5 shadow-sm">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center bg-${primaryColor}-800`}>
                <CreditCard size={15} className="text-white" />
              </div>
              <div className="flex flex-col">
                <span className="text-[0.6rem] font-bold text-slate-400 uppercase tracking-tight leading-none">
                  <Lang w="internet" />
                </span>
                <span className="text-[0.8rem] font-extrabold text-slate-800 leading-tight">
                  <VNDInline value={totalInternet} />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 items-center">
          <DateChoice
            value={dt}
            setValue={(d) => setDt(new sdate(d))}
            label={<Lang w="date" />}
            width={250}
            noFuture
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <PaymentsAndCosts
          payments={cashPayments}
          date={dt}
          paymentType={0}
          label="cash"
          costs={costs}
        />

        <PaymentsAndCosts
          payments={internetPayments}
          label="internet"
          costs={costs}
          paymentType={1}
        />
      </div>
    </div>
  );
}

/**
 * Inline VND formatter for summary chips.
 * Formats a number as Vietnamese Dong currency string.
 *
 * @param {Object} props - Component props
 * @param {number} props.value - Amount to format
 * @returns {string} Formatted currency string
 * @author Forge
 * @date 2026-04-08
 * @version 1.0
 */
function VNDInline({ value }) {
  return new Intl.NumberFormat('vi-VN').format(value || 0) + '₫';
}
